import { ArrowRight, ShieldCheck, TriangleAlert } from "lucide-react";

import type { IndustryProduct } from "@/lib/cms.types";
import { useT } from "@/lib/i18n-dict";
import { Section, SectionHeading } from "./Section";
import { Reveal } from "./Reveal";
import { LocaleLink } from "./LocaleLink";

/** Cards that map each linked product to the industry pain it removes. */
export function IndustryProducts({
  products,
  industry,
}: {
  products: IndustryProduct[];
  industry: string;
}) {
  const t = useT();

  if (!products.length) return null;

  return (
    <Section className="bg-surface/40">
      <SectionHeading
        eyebrow="Pain → solution"
        title={`${t("Solutions by industry")}: ${industry}`}
        lead="Each module and add-on below is mapped to a concrete problem on your shop floor."
      />
      <div className="mt-12 grid gap-6 md:grid-cols-2">
        {products.map((product, index) => (
          <Reveal key={product.id} delay={index * 60}>
            <article className="iso-plate flex h-full flex-col rounded-2xl border border-border bg-card p-7">
              <div className="flex items-center justify-between gap-4">
                <LocaleLink
                  to={product.href}
                  className="group inline-flex items-center gap-2 font-display text-lg font-semibold transition-colors hover:text-primary"
                >
                  {t(product.name)}
                  <ArrowRight
                    className="size-4 transition-transform group-hover:translate-x-1"
                    aria-hidden
                  />
                </LocaleLink>
                <span className="whitespace-nowrap rounded-full border border-border px-2.5 py-1 text-[0.7rem] font-semibold tracking-wide text-muted-foreground uppercase">
                  {t(product.product_type === "addon" ? "Add-on" : "Module")}
                </span>
              </div>

              {product.pain ? (
                <div className="mt-5 flex gap-3">
                  <TriangleAlert className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
                  <p className="text-base text-muted-foreground">{product.pain}</p>
                </div>
              ) : null}

              {product.outcome ? (
                <div className="mt-4 flex gap-3 border-t border-border pt-4">
                  <ShieldCheck className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
                  <p className="text-base text-foreground">{product.outcome}</p>
                </div>
              ) : null}
            </article>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}
